import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { Location } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { Bq9DataService } from "./services/bq9-data.service";
import { CONSTANTS } from "../../../../constants";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { AreaService } from "../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { JobIssueVendorService } from "../../../../domains/jobIssues/services/job-issue-vendor.service";
import { MediaService } from "../../../../domains/media/services/media.service";
import JobDataService from "../../../../domains/jobs/services/job-data-service";
import { FeatureFlag, FeatureService } from "../../../../core/feature-flags/feature.service";
import { firstValueFrom } from "rxjs";
import { JobSignatureService } from "../../../../domains/jobSignatures/services/job-signature-service";
import { VendorService } from "../../../../domains/vendors/services/vendor-service";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { MissingDataCheckOptions, MissingDataService } from "../../../../shared/utils/missing-data-service";

@Component({
  selector: "app-bq9-root",
  standalone: true,
  imports: [RouterOutlet, SkeletonModule],
  providers: [Bq9DataService],
  templateUrl: "./bq9-root.component.html",
  styleUrl: "./bq9-root.component.scss"
})
export class Bq9RootComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private jobId = 0;

  constructor(
    private route: ActivatedRoute,
    protected bq9DataService: Bq9DataService,
    private jobService: JobService,
    private lotService: LotService,
    private areasService: AreaService,
    private jobIssueService: JobIssueService,
    private jobIssueVendorService: JobIssueVendorService,
    private mediaService: MediaService,
    private jobDataService: JobDataService,
    private toastService: UiToastService,
    private router: Router,
    private location: Location,
    private featureService: FeatureService,
    private vendorService: VendorService,
    private jobSignatureService: JobSignatureService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private logger: LoggerService,
    private missingDataService: MissingDataService
  ) { }

  async ngOnInit(): Promise<void> {
    const isBq9Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ9));
    if (!isBq9Enabled) {
      this.toastService.error("The BQ9 feature is not enabled for your account. Please contact support if you believe this is an error.");
      this.router.navigate(["/cm/schedule"]);
      return;
    }

    this.jobId = parseInt(this.route.snapshot.paramMap.get("jobId") ?? "0");
    const fromLotSummary = (this.location.getState() as { fromLotSummary?: boolean })?.fromLotSummary ?? false;

    // Subscribes to the CM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    const coordinator = new BackgroundRefreshCoordinator(
      this.constructionManagerScheduleService.scheduleRefreshed,
      redirectOnMissingData => this.loadJobData(this.jobId, redirectOnMissingData),
      this.destroyRef
    );
    this.bq9DataService.isRefreshingInBackground = this.constructionManagerScheduleService.isFetchingSchedule;
    this.bq9DataService.dataUpdatedPulse = coordinator.dataUpdatedPulse;

    const loaded = await coordinator.runInitialLoad();
    if (!loaded) {
      return;
    }

    this.bq9DataService.fromLotSummary.set(fromLotSummary);
  }

  /**
   * Loads (or re-loads) all local data for the given BQ9 job. `redirectOnMissingData` should only be
   * true for the initial load — a background-refresh-triggered reload should never yank the user away
   * from a page they're already interacting with, so it just logs and no-ops instead.
   */
  private async loadJobData(jobId: number, redirectOnMissingData: boolean): Promise<boolean> {
    const bq9Job = await this.jobService.getJobById(jobId);
    const missingDataOptions = this.buildMissingDataOptions(jobId, redirectOnMissingData, bq9Job?.lotId);

    if (this.missingDataService.isMissingData(bq9Job, missingDataOptions.job)) {
      return false;
    }

    const bq8Job = await this.jobService.getJobForLotByQualityStep(bq9Job.lotId, CONSTANTS.BUILD_QUALITY_STEPS.BQ8);
    if (this.missingDataService.isMissingData(bq8Job, missingDataOptions.bq8Job)) {
      return false;
    }

    const lot = await this.lotService.getLotById(bq9Job.lotId);
    if (this.missingDataService.isMissingData(lot, missingDataOptions.lot)) {
      return false;
    }

    const areas = await this.areasService.getAreasByLotId(bq9Job.lotId);
    const bq8Issues = await this.jobIssueService.getJobIssuesForJob(bq8Job.id);
    const bq9Issues = await this.jobIssueService.getJobIssuesForJob(bq9Job.id);
    const bq9ParentIdentifiers = new Set(bq9Issues.map(i => i.parentIdentifier).filter(Boolean));
    const issues = [
      ...bq9Issues,
      ...bq8Issues.filter(i => !bq9ParentIdentifiers.has(i.identifier))
    ];
    const media = await this.mediaService.getMediaForJobIssues(issues);
    const allIssueIdentifiers = issues.map(i => i.identifier);
    const jobIssueVendors = await this.jobIssueVendorService.getAllActiveVendorsForIssues(allIssueIdentifiers);
    const vendorData = await this.vendorService.getVendorsForJob(bq9Job.id);
    const vendors = this.vendorService.processVendorData(vendorData).sort((a, b) => a.name.localeCompare(b.name));
    const bq8Signatures = await this.jobSignatureService.getSignaturesByJobId(bq8Job.id);
    const bq8HomeownerEmails = bq8Signatures
      .filter(s => !!s.signerEmail && !s.signatureSkipType && s.signerName)
      .map(s => s.signerEmail);

    await this.jobDataService.startJob(bq9Job);

    this.bq9DataService.job.set(bq9Job);
    this.bq9DataService.lot.set(lot);
    this.bq9DataService.areas.set(areas);
    this.bq9DataService.jobIssues.set(issues);
    this.bq9DataService.media.set(media);
    this.bq9DataService.jobIssueVendors.set(jobIssueVendors);
    this.bq9DataService.vendors.set(vendors);
    this.bq9DataService.bq8HomeownerEmails.set(bq8HomeownerEmails);
    return true;
  }

  private buildMissingDataOptions(jobId: number, redirectOnMissingData: boolean, lotId?: number): Record<"job" | "bq8Job" | "lot", MissingDataCheckOptions> {
    return {
      job: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading job data for BQ9. Please try again.",
        logMessage: `Background refresh could not find job ${jobId}`,
        componentTag: "Bq9RootComponent"
      },
      bq8Job: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related job data for BQ9. Please try again.",
        logMessage: `Background refresh could not find related BQ8 job for lot ${lotId}`,
        componentTag: "Bq9RootComponent"
      },
      lot: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related lot data for BQ9. Please try again.",
        logMessage: `Background refresh could not find lot ${lotId}`,
        componentTag: "Bq9RootComponent"
      }
    };
  }
}
